import { FullProject, User, projectStatusByCode } from 'entities';

import { FilterOption, ListFilters } from 'components/ListManipulation';
import { Dot, Row } from 'components/Blocks';
import { getUserLabel } from 'components/Inputs/SelectLabels';

import { PROJECT_TYPE_OPTIONS } from 'utils/constants';
import { sortByName } from 'utils/array';
import { getCountryLabel } from 'utils/countries';

export function getProjectFilters(): ListFilters<FullProject>[] {
  const filters: (ListFilters<FullProject> | null)[] = [
    {
      getLabel: (t) => t('status'),

      property: 'status',

      multiple: true,

      optionExtractor: ({ status }, t) => ({
        value: status,
        name: projectStatusByCode[status].getLabel(t),
      }),

      getOptionNode: ({ name, value }) => (
        <Row gap="1.5">
          <Dot color={projectStatusByCode[value].color} />

          <span>{name}</span>
        </Row>
      ),
    },

    {
      getLabel: (t) => t('owner'),

      property: 'owner',

      multiple: true,

      optionExtractor: ({ owner }) => ({
        ...owner,
        value: owner.id,
      }),

      getOptionNode: (user) => getUserLabel(user as unknown as User),
    },

    {
      getLabel: (t) => t('task-owners'),

      property: 'tasks',

      id: 'task-owners',

      multiple: true,

      optionExtractor: ({ tasks }) =>
        tasks.map(({ owner }) => ({
          ...owner,
          value: owner.id,
        })),

      getOptionNode: (user) => getUserLabel(user as unknown as User),
    },

    {
      getLabel: (t) => t('task-sub-owners'),

      property: 'tasks',

      id: 'task-sub-owners',

      multiple: true,

      optionExtractor: ({ tasks }) =>
        tasks
          .map(({ subOwner }) =>
            subOwner
              ? {
                  ...subOwner,
                  value: subOwner?.id,
                }
              : null,
          )
          .filter(Boolean) as FilterOption[],

      getOptionNode: (user) => getUserLabel(user as unknown as User),
    },

    {
      getLabel: (t) => t('tasks'),

      property: 'tasks',

      multiple: true,

      optionExtractor: ({ tasks }) =>
        tasks.map(({ baseName }) => ({
          value: baseName,
          name: baseName,
        })),
    },

    {
      getLabel: (t) => t('client'),

      property: 'client',

      multiple: true,

      optionExtractor: ({ client, clientName }) => ({
        name: clientName,
        value: client,
      }),
    },

    {
      getLabel: (t) => t('type'),

      property: 'type',

      multiple: true,

      optionExtractor: ({ type }, t) => ({
        name: PROJECT_TYPE_OPTIONS.find(({ value }) => value === type)?.getLabel(t) ?? '',
        value: type,
      }),
    },

    {
      getLabel: (t) => t('country'),

      property: 'countries',

      multiple: true,

      optionExtractor: ({ countries }) =>
        countries.map((country) => ({
          name: getCountryLabel(country),
          value: country,
        })),
    },

    {
      getLabel: (t) => t('tags'),

      property: 'tags',

      multiple: true,

      optionExtractor: ({ tags, teamResponsible }) =>
        tags.map(({ name, id, color }) => ({
          value: id ?? `${teamResponsible}-${name}`,
          name,
          color,
        })),

      getOptionNode: ({ name, color }) => (
        <Row gap="1.5">
          <Dot color={color} />

          <span>{name}</span>
        </Row>
      ),
    },

    {
      getLabel: (t) => t('task-tags'),

      property: 'tasks',

      id: 'task-tags',

      multiple: true,

      optionExtractor: ({ tasks }) =>
        tasks
          .map(({ tags }) => tags)
          .flat()
          .filter((t) => t.tag)
          .map(({ tag }) => ({
            value: tag.id,
            name: tag.name,
            color: tag.color,
          })),

      getOptionNode: ({ name, color }) => (
        <Row gap="1.5">
          <Dot color={color} />

          <span>{name}</span>
        </Row>
      ),
    },

    {
      getLabel: (t) => t('sales-person'),

      property: 'salesPerson',

      multiple: true,

      optionExtractor: ({ salesPerson }) => ({
        value: salesPerson,
        name: salesPerson,
      }),

      getOptionNode: (user) => getUserLabel(user as unknown as User),

      getLoadConfig: (options) => ({
        blockLoad: !options.length,
        target: 'users.bulkFind',

        params: {
          ids: options.map(({ value }) => value).filter(Boolean),
        },

        parser: (users) =>
          sortByName(
            users.map((user) => ({
              ...user,
              value: user.id,
            })),
          ),
      }),
    },

    {
      getLabel: (t) => t('team-responsible'),

      property: 'teamResponsible',

      multiple: true,

      optionExtractor: ({ teamResponsible }) => ({
        value: teamResponsible,
        name: teamResponsible,
      }),

      getLoadConfig: (options) => ({
        blockLoad: !options.length,
        target: 'teams.list',
        parser: (teams) =>
          sortByName(
            teams
              .filter(({ id }) => options.find(({ value }) => id === value))
              .map((team) => ({
                ...team,
                value: team.id,
                name: team.name,
              })),
          ),
      }),
    },
  ];

  return filters.filter(Boolean) as ListFilters<FullProject>[];
}
